import { RetailInvoicePDFProps } from 'src/components/RetailInvoice/RetailInvoicePDF.types';
import { getPerformerAndVenueForEvent } from 'src/utils/eventWithDataUtils';
import { compareByNumericPrefix } from 'src/utils/tableUtils';
import {
  CatalogResults,
  EventWithData,
  ListingDetails,
  RetailSaleListingTickets,
} from 'src/WebApiController';

import { RetailSaleFormValues } from './types';

type OrderDetailRow = RetailInvoicePDFProps['orderDetailRows'][number];

/**
 * POS-8630: builds the invoice ORDER DETAIL rows (one per sale = one listing)
 * shared by the retail-sale invoice preview (RetailSaleInvoicePreviewStep) and
 * the retail-sale download handler (RetailSale.tsx).
 *
 * Multi-listing mode: exactly ONE row per distinct `listingId` (one sale = one
 * listing). All of a listing's per-(section,row) selections are aggregated into
 * a single row. The event and venue are resolved PER LISTING via
 * listingTickets.viagVirtId -> eventsTransformed.event.virtId (so a multi-event
 * sale shows the correct event on each row). Single-listing mode preserves the
 * original single-row behavior.
 */
export const buildOrderDetailRows = ({
  values,
  event,
  venue,
  totalAmount,
  listing,
  listingTickets,
  eventsTransformed,
  catalogData,
}: {
  values: RetailSaleFormValues;
  event: EventWithData | undefined;
  venue: { name?: string } | undefined;
  totalAmount: number;
  listing?: ListingDetails;
  listingTickets?: RetailSaleListingTickets[];
  eventsTransformed?: EventWithData[];
  catalogData?: CatalogResults;
}): OrderDetailRow[] => {
  if (values.perListingSelections?.length) {
    // POS-8630: group selections by distinct listingId (one sale = one listing),
    // preserving first-seen order of listingIds. Each listing may span multiple
    // (section,row) selections, but must emit exactly ONE aggregated row.
    type SelectionRow = (typeof values.perListingSelections)[number];
    const groups = new Map<number, SelectionRow[]>();
    for (const sel of values.perListingSelections) {
      const existing = groups.get(sel.listingId);
      if (existing) {
        existing.push(sel);
      } else {
        groups.set(sel.listingId, [sel]);
      }
    }

    return Array.from(groups.entries()).flatMap<OrderDetailRow>(
      ([listingId, selections]) => {
        // POS-8630: filter out excluded selections (qtyToSell === 0 means the
        // seller zeroed that (section,row) group out of the sale) so their
        // seats/rows/section don't leak into the aggregate.
        const soldSelections = selections.filter((s) => s.qtyToSell > 0);
        // If every selection for this listing is excluded, omit the listing
        // entirely rather than emitting a blank/zero row.
        if (soldSelections.length === 0) {
          return [];
        }

        const lt = listingTickets?.find((t) => t.listingId === listingId);
        const evt = eventsTransformed?.find(
          (e) => e.event?.virtId === lt?.viagVirtId
        );
        // Resolve event/venue PER LISTING (uniform across the listing's
        // selections). getPerformerAndVenueForEvent needs the raw catalog
        // (performers/venues) to resolve the venue.
        // POS-8630: if the per-listing event/venue isn't resolvable yet (catalog
        // data not loaded, or listingTickets not ready), fall back to the primary
        // event/venue so the row still renders something sensible.
        const { venue: rowVenue } = getPerformerAndVenueForEvent(
          evt?.event ?? null,
          catalogData
        );

        // Section is uniform per listing; take the first sold selection's section.
        const section = soldSelections[0]?.section ?? undefined;

        // Rows may differ across a listing's selections. Collect the DISTINCT
        // non-empty rows and, if more than one, join them for a readable display
        // (e.g. "A, B"). compareByNumericPrefix gives numeric-aware ordering.
        const distinctRows = Array.from(
          new Set(
            soldSelections
              .map((s) => s.row)
              .filter((r): r is string => !!r && r.length > 0)
          )
        ).toSorted(compareByNumericPrefix);
        const row =
          distinctRows.length === 0 ? undefined : distinctRows.join(', ');

        // seatFrom = earliest (min) seatFrom; seatTo = latest (max) seatTo, both
        // using compareByNumericPrefix for numeric-aware seat ordering. Ignore
        // null/empty seat values.
        const seatFroms = soldSelections
          .map((s) => s.seatFrom)
          .filter((s): s is string => !!s && s.length > 0);
        const seatTos = soldSelections
          .map((s) => s.seatTo)
          .filter((s): s is string => !!s && s.length > 0);
        const seatFrom =
          seatFroms.length === 0
            ? undefined
            : seatFroms.reduce((min, cur) =>
                compareByNumericPrefix(cur, min) < 0 ? cur : min
              );
        const seatTo =
          seatTos.length === 0
            ? undefined
            : seatTos.reduce((max, cur) =>
                compareByNumericPrefix(cur, max) > 0 ? cur : max
              );

        const qty = soldSelections.reduce((sum, s) => sum + s.qtyToSell, 0);
        // unitProceeds is PER UNIT (line total = qtyToSell * unitProceeds).
        const proceed = soldSelections.reduce(
          (sum, s) => sum + s.qtyToSell * s.unitProceeds,
          0
        );

        return [
          {
            eventName: evt?.event?.name ?? event?.event?.name,
            eventDate:
              evt?.event?.dates?.start ??
              event?.event?.dates?.start ??
              undefined,
            venueName: rowVenue?.name ?? venue?.name,
            section,
            row,
            seatFrom,
            seatTo,
            qty,
            proceed,
          },
        ];
      }
    );
  }

  // Single-listing mode — preserve existing behavior.
  return [
    {
      eventName: event?.event?.name,
      eventDate: event?.event?.dates?.start ?? undefined,
      venueName: venue?.name,
      section: listing?.seating?.section ?? values.section ?? undefined,
      row: listing?.seating?.row ?? values.row ?? undefined,
      seatFrom: listing?.seating?.seatFr ?? values.seatFrom ?? undefined,
      seatTo: listing?.seating?.seatTo ?? values.seatTo ?? undefined,
      qty: listing?.availQty ?? values.quantitySold ?? undefined,
      proceed: totalAmount,
    },
  ];
};
